import { useEffect, useRef } from "react";
import { MapPin, Phone, Mail, Instagram, Clock, Calendar, MessageCircle, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getPlanityUrl } from "@/config/standort";
import { Button } from "@/components/ui/button";


const Contact = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const planityUrl = getPlanityUrl();
  const basePath = "/nuernberg";
  const address = "Deidesheimer Str. 4\n90469 Nürnberg";
  const phoneDisplay = "+49 151 7253 0302";
  const phoneTel = "+4915172530302";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="kontakt" className="py-16 md:py-24 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/50 to-secondary/80" />
      
      <div className="container px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8">
            <div className="inline-block px-8 py-5 rounded-2xl bg-foreground/5 backdrop-blur-md border border-foreground/10">
              <div className="flex items-center justify-center gap-4 mb-3">
                <span className="w-12 h-px bg-gradient-to-r from-transparent to-highlight" />
                <span className="text-sm tracking-wide uppercase text-label font-medium">Kontakt & Termine</span>
                <span className="w-12 h-px bg-gradient-to-l from-transparent to-highlight" />
              </div>
              <h2 className="text-heading md:text-display font-serif font-light text-foreground">
                Wir freuen uns <span className="text-highlight font-medium">auf dich</span>
              </h2>
            </div>
          </div>

          {/* Two Booking Options */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* Option 1: Planity */}
            <a
              href={planityUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group block animate-on-scroll opacity-0 translate-y-8"
            >
              <div className="h-full p-8 md:p-10 rounded-2xl bg-charcoal text-white transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:shadow-charcoal/30">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all duration-300">
                    <Calendar className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-medium">Online buchen</h3>
                    <p className="text-white/60 text-sm">via Planity</p>
                  </div>
                </div>
                <p className="text-white/70 mb-6">
                  Wähle deinen Wunschtermin und buche direkt online – schnell und unkompliziert.
                </p>
                <div className="flex items-center gap-2 text-white font-medium group-hover:gap-4 transition-all duration-300">
                  <span>Termin buchen</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </a>

            {/* Option 2: Anfrage */}
            <Link 
              to={`${basePath}/anfrage`} 
              className="group block animate-on-scroll opacity-0 translate-y-8"
              style={{ animationDelay: "100ms" }}
            >
              <div className="h-full p-8 md:p-10 rounded-2xl bg-white border border-border transition-all duration-500 hover:shadow-xl hover:-translate-y-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center group-hover:bg-primary/30 group-hover:scale-110 transition-all duration-300">
                    <MessageCircle className="w-7 h-7 text-foreground" />
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-medium text-foreground">Anfrage senden</h3>
                    <p className="text-muted-foreground text-sm">Kontaktformular</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  Du hast Fragen oder spezielle Wünsche? Schreib uns – wir melden uns bei dir.
                </p>
                <div className="flex items-center gap-2 text-foreground font-medium group-hover:gap-4 transition-all duration-300">
                  <span>Zum Formular</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>

          {/* Contact Info Grid */}
          <div className="animate-on-scroll opacity-0 translate-y-8 grid md:grid-cols-3 gap-8 p-8 md:p-10 rounded-2xl bg-white border border-border" style={{ animationDelay: "200ms" }}>
            {/* Left: Address & Contact */}
            <div className="space-y-6">
              <h4 className="font-serif text-lg font-medium text-foreground mb-4">So erreichst du uns</h4>
              
              <div className="flex items-start gap-3 group">
                <MapPin className="w-5 h-5 text-highlight mt-0.5 shrink-0 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-foreground font-medium">Adresse</p>
                  <p className="text-muted-foreground text-sm">
                    {address.split("\n").map((line) => <span key={line} className="block">{line}</span>)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <Phone className="w-5 h-5 text-highlight mt-0.5 shrink-0 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-foreground font-medium">Telefon</p>
                  <a href={`tel:${phoneTel}`} className="text-muted-foreground text-sm hover:text-highlight transition-colors">
                    {phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <Mail className="w-5 h-5 text-highlight mt-0.5 shrink-0 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-foreground font-medium">E-Mail</p>
                  <a href="mailto:info@22studios.de" className="text-muted-foreground text-sm hover:text-highlight transition-colors">
                    info@22studios.de
                  </a>
                </div>
              </div>
            </div>

            {/* Middle: Opening Hours */}
            <div>
              <h4 className="font-serif text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-highlight" />
                Öffnungszeiten
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between hover:bg-secondary/50 px-2 py-1 rounded transition-colors">
                  <span className="text-muted-foreground">Montag</span>
                  <span className="text-foreground">geschlossen</span>
                </div>
                <div className="flex justify-between hover:bg-secondary/50 px-2 py-1 rounded transition-colors">
                  <span className="text-muted-foreground">Di – Mi</span>
                  <span className="text-foreground">9:00 – 18:00</span>
                </div>
                <div className="flex justify-between hover:bg-secondary/50 px-2 py-1 rounded transition-colors">
                  <span className="text-muted-foreground">Do – Fr</span>
                  <span className="text-foreground">10:00 – 20:00</span>
                </div>
                <div className="flex justify-between hover:bg-secondary/50 px-2 py-1 rounded transition-colors">
                  <span className="text-muted-foreground">Samstag</span>
                  <span className="text-foreground">9:00 – 16:00</span>
                </div>
                <div className="flex justify-between hover:bg-secondary/50 px-2 py-1 rounded transition-colors">
                  <span className="text-muted-foreground">Sonntag</span>
                  <span className="text-foreground">geschlossen</span>
                </div>
              </div>
            </div>

            {/* Right: Social & CTA */}
            <div className="flex flex-col justify-between">
              <div>
                <h4 className="font-serif text-lg font-medium text-foreground mb-4">Folge uns</h4>
                <a
                  href="https://instagram.com/22studios.nue"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-secondary hover:bg-primary/20 transition-all duration-300 hover:scale-105"
                >
                  <Instagram className="w-5 h-5 text-foreground" />
                  <span className="text-foreground font-medium">@22studios.nue</span>
                </a>
              </div>
              
              <div className="mt-6">
                <Button
                  size="lg"
                  className="w-full bg-charcoal text-white hover:bg-charcoal/90 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
                  onClick={() => window.open(planityUrl, '_blank')}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Jetzt Termin buchen
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
